import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaShieldAlt, 
  FaChartLine, 
  FaUsers, 
  FaExternalLinkAlt, 
  FaFileAlt, 
  FaLaptop, 
  FaClipboardCheck, 
  FaNetworkWired, 
  FaLock, 
  FaRobot,
  FaCheckCircle,
  FaTag,
  FaIndustry,
  FaBolt,
  FaUniversity,
  FaHospital,
  FaDatabase,
  FaWarehouse,
  FaHandHoldingHeart,
  FaStore,
  FaMicrochip,
  FaSatelliteDish,
  FaTruck,
  FaGlobe,
  FaChartBar,
  FaLandmark,
  FaCloudscale,
  FaFlag,
  FaMapMarkedAlt,
  FaFilm,
  FaPoundSign,
  FaComment,
  FaUser
} from 'react-icons/fa';

// Import the icons causing ESLint errors separately
import { FaExclamationTriangle, FaPlane, FaGraduationCap } from 'react-icons/fa';
import LogoImage from '../common/LogoImage';

const LandingPage = () => {
  // Color scheme from user preferences
  const colors = {
    primary: '#243444',
    secondary: '#D2BA7B',
    light: '#FFFAFA',
    gray: '#4C5454',
    danger: '#7C1227'
  };

  const styles = {
    container: {
      backgroundColor: colors.light,
      minHeight: '100vh',
      fontFamily: '"Poppins", sans-serif',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
      maxWidth: '100%',
    },
    header: {
      backgroundColor: colors.primary,
      color: colors.light,
      padding: '15px 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      width: '100%'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    logoText: {
      color: colors.secondary,
      marginLeft: '10px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    nav: {
      display: 'flex',
      gap: '30px',
      alignItems: 'center'
    },
    navLink: {
      color: colors.light,
      textDecoration: 'none',
      fontWeight: '500',
      fontSize: '1.05rem',
      padding: '5px 10px',
      transition: 'color 0.3s'
    },
    button: {
      backgroundColor: colors.secondary,
      color: colors.primary,
      padding: '10px 20px',
      borderRadius: '5px',
      fontWeight: 'bold',
      textDecoration: 'none',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s'
    },
    hero: {
      backgroundImage: 'linear-gradient(135deg, rgba(36,52,68,0.95) 0%, rgba(36,52,68,0.85) 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: colors.light,
      textAlign: 'center',
      padding: '160px 20px 100px',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContent: {
      maxWidth: '900px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 5,
    },
    heroTitle: {
      fontSize: '3.5rem',
      marginBottom: '25px',
      fontWeight: 'bold',
      color: colors.light,
      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
      letterSpacing: '0.5px',
    },
    heroHighlightText: {
      color: colors.secondary,
      display: 'inline',
      fontSize: 'inherit',
      fontWeight: 'inherit',
    },
    heroSubtitle: {
      fontSize: '1.5rem',
      marginBottom: '40px',
      color: '#FFFAFA',
      lineHeight: 1.7,
      maxWidth: '750px',
      margin: '0 auto 40px',
      fontWeight: '300',
    },
    heroCta: {
      display: 'flex',
      justifyContent: 'center',
      gap: '20px',
      marginTop: '40px',
    },
    heroHighlight: {
      color: colors.secondary,
      fontWeight: '500',
    },
    heroButton: {
      backgroundColor: colors.secondary,
      color: colors.primary,
      padding: '15px 35px',
      borderRadius: '30px',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      textDecoration: 'none',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(210,186,123,0.3)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
    },
    heroButtonSecondary: {
      backgroundColor: 'transparent',
      color: colors.light,
      padding: '14px 30px',
      borderRadius: '30px',
      fontSize: '1.1rem',
      fontWeight: '500',
      textDecoration: 'none',
      border: '1px solid rgba(255,255,255,0.3)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    },
    section: {
      padding: '80px 40px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: '2.5rem',
      color: colors.primary,
      textAlign: 'center',
      marginBottom: '20px',
      position: 'relative',
      fontWeight: 'bold'
    },
    sectionDivider: {
      width: '80px',
      height: '4px',
      backgroundColor: colors.secondary,
      margin: '0 auto 40px'
    },
    sectionDescription: {
      fontSize: '1.2rem',
      color: colors.gray,
      textAlign: 'center',
      maxWidth: '800px',
      margin: '0 auto 40px',
      lineHeight: 1.6
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '25px',
      marginTop: '40px',
    },
    featureCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '30px 20px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    featureIcon: {
      marginBottom: '20px',
      color: colors.primary
    },
    featureTitle: {
      fontSize: '1.4rem',
      color: colors.primary,
      marginBottom: '15px',
      fontWeight: 'bold',
      textAlign: 'center'
    },
    featureDescription: {
      color: colors.gray,
      textAlign: 'center',
      lineHeight: '1.6',
    },
    discountBanner: {
      backgroundColor: colors.secondary,
      color: colors.primary,
      padding: '15px',
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '1.2rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '40px',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    discountCode: {
      backgroundColor: colors.primary,
      color: colors.light,
      padding: '5px 10px',
      borderRadius: '4px',
      marginLeft: '10px',
      marginRight: '10px',
      fontFamily: 'monospace',
      letterSpacing: '1px'
    },
    pricingSection: {
      backgroundColor: '#f5f5f5',
      padding: '80px 40px',
    },
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '30px',
      maxWidth: '1200px',
      margin: '40px auto 0',
    },
    pricingCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '40px 30px',
      boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
      textAlign: 'center',
      transition: 'transform 0.3s, box-shadow 0.3s',
      display: 'flex',
      flexDirection: 'column',
    },
    pricingTitle: {
      fontSize: '1.8rem',
      color: colors.primary,
      marginBottom: '20px',
      fontWeight: 'bold',
    },
    pricingPrice: {
      fontSize: '2.5rem',
      color: colors.primary,
      marginBottom: '20px',
      fontWeight: 'bold',
    },
    pricingPeriod: {
      fontSize: '1rem',
      color: colors.gray,
    },
    pricingFeatures: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 30px',
      textAlign: 'left',
    },
    pricingButton: {
      backgroundColor: colors.primary,
      color: colors.secondary,
      padding: '12px 20px',
      borderRadius: '5px',
      textDecoration: 'none',
      fontWeight: 'bold',
      marginTop: 'auto',
      display: 'inline-block',
      transition: 'background-color 0.3s'
    },
    aboutSection: {
      padding: '80px 40px',
      backgroundColor: colors.light,
    },
    aboutContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    aboutText: {
      color: colors.gray,
      lineHeight: '1.8',
      maxWidth: '800px',
      margin: '0 auto',
      textAlign: 'center',
    },
    servicesList: {
      listStyle: 'none',
      padding: 0,
      margin: '30px 0',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '15px',
      maxWidth: '800px',
    },
    serviceItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: colors.gray,
    },
    serviceIcon: {
      color: colors.secondary,
    },
    footer: {
      backgroundColor: colors.primary,
      color: colors.light,
      padding: '60px 40px 30px',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px',
    },
    footerLogo: {
      color: colors.secondary,
      fontSize: '1.5rem',
      fontWeight: 'bold',
      marginBottom: '20px',
      display: 'block',
    },
    footerLinks: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    footerLink: {
      margin: '10px 0',
    },
    footerLinkAnchor: {
      color: '#aaa',
      textDecoration: 'none',
      transition: 'color 0.3s',
    },
    footerTitle: {
      color: colors.secondary,
      marginBottom: '20px',
      fontSize: '1.2rem',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255,255,255,0.1)',
      marginTop: '40px',
      paddingTop: '20px',
      textAlign: 'center',
      color: '#aaa',
    },
    externalLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      color: colors.secondary,
      textDecoration: 'none',
      fontWeight: 'bold',
    }
  };

  return (
    <>
      {/* Full-width hero background that extends edge-to-edge */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '700px',
        backgroundColor: colors.primary,
        zIndex: 0,
        overflow: 'hidden',
      }}>
        {/* Modern abstract background elements */}
        <div style={{position: 'absolute', width: '100%', height: '100%', opacity: 0.2, zIndex: 0}}>
          {/* Compass/Map Elements - modernized */}
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '650px', height: '650px', border: '2px solid #D2BA7B', borderRadius: '50%', boxShadow: '0 0 40px rgba(210,186,123,0.3)'}}></div>
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '500px', height: '500px', border: '1px solid #D2BA7B', borderRadius: '50%'}}></div>
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(0deg)', width: '800px', height: '2px', background: 'linear-gradient(90deg, rgba(210,186,123,0) 0%, rgba(210,186,123,1) 50%, rgba(210,186,123,0) 100%)'}}></div>
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(90deg)', width: '800px', height: '2px', background: 'linear-gradient(90deg, rgba(210,186,123,0) 0%, rgba(210,186,123,1) 50%, rgba(210,186,123,0) 100%)'}}></div>
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(45deg)', width: '800px', height: '2px', background: 'linear-gradient(90deg, rgba(210,186,123,0) 0%, rgba(210,186,123,1) 50%, rgba(210,186,123,0) 100%)'}}></div>
          <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-45deg)', width: '800px', height: '2px', background: 'linear-gradient(90deg, rgba(210,186,123,0) 0%, rgba(210,186,123,1) 50%, rgba(210,186,123,0) 100%)'}}></div>
        </div>
        
        {/* Floating particles/dots */}
        <div style={{position: 'absolute', top: '20%', left: '10%', width: '12px', height: '12px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.6}}></div>
        <div style={{position: 'absolute', top: '70%', left: '15%', width: '8px', height: '8px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.4}}></div>
        <div style={{position: 'absolute', top: '30%', left: '85%', width: '15px', height: '15px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.5}}></div>
        <div style={{position: 'absolute', top: '60%', left: '80%', width: '10px', height: '10px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.3}}></div>
        <div style={{position: 'absolute', top: '40%', left: '25%', width: '20px', height: '20px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.2}}></div>
        <div style={{position: 'absolute', top: '80%', left: '60%', width: '14px', height: '14px', backgroundColor: colors.secondary, borderRadius: '50%', opacity: 0.3}}></div>
      </div>
      
      <div style={{...styles.container, padding: 0, margin: 0, width: '100%', maxWidth: '100%'}}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.logoContainer}>
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <LogoImage 
                type="white" 
                height={40} 
              />
              <span style={styles.logoText}>Business Resilience Navigator</span>
            </Link>
          </div>
          <nav style={styles.nav}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#pricing" style={styles.navLink}>Pricing</a>
            <a href="#scenario-ttx" style={styles.navLink}>Scenario TTX</a>
            <a href="#about" style={styles.navLink}>About</a>
            <Link to="/login" style={styles.navLink}>Login</Link>
            <Link to="/register" style={styles.button}>Get Started</Link>
          </nav>
        </header>

        {/* Hero Content */}
        <div style={{
          position: 'relative',
          color: colors.light,
          textAlign: 'center',
          padding: '140px 0 80px',
          margin: 0,
          width: '100%',
          zIndex: 1,
        }}>
        
        <div style={{...styles.heroContent, position: 'relative', zIndex: 2}}>
          <h1 style={styles.heroTitle}>
            Transform Your Organization's Resilience Strategy
          </h1>
          
          <p style={styles.heroSubtitle}>
            Comprehensive assessments to measure and enhance your organization's resilience across all critical operational domains
          </p>
          
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '40px', flexWrap: 'wrap'}}>
            <Link to="/register" style={styles.heroButton}>
              <FaShieldAlt size={18} /> Start Your Assessment
            </Link>
            
            <Link to="/login" style={styles.heroButtonSecondary}>
              <FaUser size={16} /> Sign In
            </Link>
          </div>
          
          {/* Stats Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '80px',
            marginTop: '60px',
            flexWrap: 'wrap',
            padding: '20px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }}>
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '2.2rem', fontWeight: 'bold', color: colors.secondary}}>22+</div>
              <div style={{color: colors.light, fontSize: '0.9rem', marginTop: '5px'}}>Industry Assessments</div>
            </div>
            
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '2.2rem', fontWeight: 'bold', color: colors.secondary}}>10+</div>
              <div style={{color: colors.light, fontSize: '0.9rem', marginTop: '5px'}}>Global Standards</div>
            </div>
            
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '2.2rem', fontWeight: 'bold', color: colors.secondary}}>1600+</div>
              <div style={{color: colors.light, fontSize: '0.9rem', marginTop: '5px'}}>Assessment Questions</div>
            </div>
          </div>
          
          {/* Discount Banner */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            marginTop: '40px',
            backgroundColor: 'rgba(210,186,123,0.15)',
            padding: '10px 20px',
            borderRadius: '30px',
            width: 'fit-content',
            margin: '40px auto 0',
          }}>
            <FaTag size={16} color={colors.secondary} />
            <span style={{color: colors.light}}>Use code </span>
            <span style={{backgroundColor: colors.secondary, color: colors.primary, padding: '4px 10px', borderRadius: '4px', fontWeight: 'bold'}}>
              NAV500OFF
            </span>
            <span style={{color: colors.light}}>for $500 off your first year with annual subscription!</span>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" style={styles.section}>
        <h2 style={styles.sectionTitle}>Comprehensive Assessment Features</h2>
        <div style={styles.sectionDivider}></div>
        <p style={styles.sectionDescription}>
          The Business Resilience Navigator offers 21 industry-specific assessments aligned with global standards and regulations including FFIEC, ISO 22301, ISO 31000, DORA, EU AI Act, CCA, CIRMP, FCA/PRA, NIST, and other regional compliance frameworks
        </p>
        
        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaRobot size={40} />
            </div>
            <h3 style={styles.featureTitle}>AI Act (EU)</h3>
            <p style={styles.featureDescription}>
              Compliance assessment for organizations developing or deploying AI systems under the EU AI Act, addressing risk management and governance requirements.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaPlane size={40} />
            </div>
            <h3 style={styles.featureTitle}>Aviation</h3>
            <p style={styles.featureDescription}>
              Tailored assessment for aviation organizations focusing on operational continuity, safety protocols, and regulatory compliance.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaCloudscale size={40} />
            </div>
            <h3 style={styles.featureTitle}>CCA (EU)</h3>
            <p style={styles.featureDescription}>
              Critical entities assessment under the EU's Critical Entities Resilience Directive, focusing on essential service continuity and infrastructure protection.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaMapMarkedAlt size={40} />
            </div>
            <h3 style={styles.featureTitle}>CIRMP (AUS)</h3>
            <p style={styles.featureDescription}>
              Australian Critical Infrastructure Risk Management Program assessment addressing security, resilience, and governance for essential service providers.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaIndustry size={40} />
            </div>
            <h3 style={styles.featureTitle}>Critical Infrastructure</h3>
            <p style={styles.featureDescription}>
              Comprehensive assessment for critical infrastructure providers focusing on service continuity and public safety requirements.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaLandmark size={40} />
            </div>
            <h3 style={styles.featureTitle}>DORA</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for financial entities under the EU's Digital Operational Resilience Act, focusing on ICT risk management and digital resilience.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaGraduationCap size={40} />
            </div>
            <h3 style={styles.featureTitle}>Education</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for educational institutions addressing campus safety, learning continuity, and student/staff wellbeing.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaBolt size={40} />
            </div>
            <h3 style={styles.featureTitle}>Energy & Utilities</h3>
            <p style={styles.featureDescription}>
              Targeted assessment for energy and utility providers addressing grid resilience, service restoration, and emergency response.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaFilm size={40} />
            </div>
            <h3 style={styles.featureTitle}>Entertainment</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for entertainment venues and production companies addressing event safety, business continuity, and digital content protection.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaPoundSign size={40} />
            </div>
            <h3 style={styles.featureTitle}>FCA PRA (UK)</h3>
            <p style={styles.featureDescription}>
              UK financial services assessment aligned with FCA and PRA operational resilience requirements, focusing on important business services and impact tolerance.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaUniversity size={40} />
            </div>
            <h3 style={styles.featureTitle}>Financial</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for financial institutions aligned with FFIEC guidelines, focusing on operational resilience and customer service continuity.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaHospital size={40} />
            </div>
            <h3 style={styles.featureTitle}>Healthcare</h3>
            <p style={styles.featureDescription}>
              Comprehensive assessment for healthcare providers addressing patient care continuity, facility safety, and medical resource management.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaDatabase size={40} />
            </div>
            <h3 style={styles.featureTitle}>Information Security</h3>
            <p style={styles.featureDescription}>
              Specialized assessment focusing on cybersecurity preparedness, data protection, and IT incident response capabilities.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaWarehouse size={40} />
            </div>
            <h3 style={styles.featureTitle}>Manufacturing</h3>
            <p style={styles.featureDescription}>
              Targeted assessment for manufacturing organizations addressing production continuity, supply chain resilience, and facility safety.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaHandHoldingHeart size={40} />
            </div>
            <h3 style={styles.featureTitle}>Non-Profit</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for non-profit organizations focusing on mission continuity, volunteer management, and community service resilience.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaFlag size={40} />
            </div>
            <h3 style={styles.featureTitle}>ORQuest Assessment (EU)</h3>
            <p style={styles.featureDescription}>
              European Union standardized assessment for organizational resilience compliance with EU regulations, business continuity protocols, and cross-border operations.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaShieldAlt size={40} />
            </div>
            <h3 style={styles.featureTitle}>Public Sector</h3>
            <p style={styles.featureDescription}>
              Tailored assessment for government agencies focusing on public service continuity, emergency coordination, and community resilience.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaShieldAlt size={40} />
            </div>
            <h3 style={styles.featureTitle}>Realistic Resilience Assessment</h3>
            <p style={styles.featureDescription}>
              A streamlined 59-question assessment for evaluating organizational resilience in realistic scenarios with practical recommendations.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaStore size={40} />
            </div>
            <h3 style={styles.featureTitle}>Retail & E-Commerce</h3>
            <p style={styles.featureDescription}>
              Tailored assessment for retail businesses addressing customer service continuity, inventory management, and digital platform resilience.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaMicrochip size={40} />
            </div>
            <h3 style={styles.featureTitle}>Technology</h3>
            <p style={styles.featureDescription}>
              Comprehensive assessment for technology companies focusing on service availability, data protection, and rapid incident response.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaSatelliteDish size={40} />
            </div>
            <h3 style={styles.featureTitle}>Telecommunications</h3>
            <p style={styles.featureDescription}>
              Specialized assessment for telecommunications providers addressing network resilience, service restoration, and emergency communications.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaTruck size={40} />
            </div>
            <h3 style={styles.featureTitle}>Transportation & Logistics</h3>
            <p style={styles.featureDescription}>
              Targeted assessment for transportation and logistics organizations focusing on supply chain continuity, fleet management, and delivery resilience.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaGlobe size={40} />
            </div>
            <h3 style={styles.featureTitle}>Universal Resilience Assessment</h3>
            <p style={styles.featureDescription}>
              Our comprehensive assessment with 180 questions to establish a detailed baseline resilience score across all key domains.
            </p>
          </div>
        </div>
        
        <h2 style={{...styles.sectionTitle, marginTop: '80px'}}>Key Benefits</h2>
        <div style={styles.sectionDivider}></div>
        
        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaClipboardCheck size={40} />
            </div>
            <h3 style={styles.featureTitle}>Best Practices</h3>
            <p style={styles.featureDescription}>
              Tailored resilience assessment based on industry best practices and standards to ensure your program meets or exceeds requirements.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaChartLine size={40} />
            </div>
            <h3 style={styles.featureTitle}>Program Maturity</h3>
            <p style={styles.featureDescription}>
              Evaluate the maturity level of your resilience program across key areas, providing a clear understanding of current strengths and areas needing enhancement.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaFileAlt size={40} />
            </div>
            <h3 style={styles.featureTitle}>Gap Analysis</h3>
            <p style={styles.featureDescription}>
              Identify gaps and deficiencies in your resilience program and receive actionable recommendations on how to close them effectively.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaChartLine size={40} />
            </div>
            <h3 style={styles.featureTitle}>Track Progress</h3>
            <p style={styles.featureDescription}>
              Track progress over time through repeated assessments, allowing you to measure improvements and ensure alignment with your resilience goals.
            </p>
          </div>
        </div>
      </section>

      {/* Placeholder for spacing */}
      <div style={{height: '80px'}}></div>
      
      {/* Pricing Section */}
      <section id="pricing" style={{...styles.section, backgroundColor: '#f9f9f9'}}>
        <h2 style={styles.sectionTitle}>Navigator Pricing</h2>
        <div style={styles.sectionDivider}></div>
        <p style={styles.sectionDescription}>
          Choose the plan that best fits your organization's needs. All plans include access to our comprehensive assessment library and AI-powered roadmap.
        </p>
        
        <div style={styles.discountBanner}>
          <FaTag size={24} />
          <span>Special Offer: Use code <span style={styles.discountCode}>NAV500OFF</span> at checkout to save $500 on your first year with annual subscription!</span>
        </div>
        
        <div style={styles.pricingGrid}>
          <div style={styles.pricingCard}>
            <h3 style={styles.pricingTitle}>Monthly Subscription</h3>
            <div style={styles.pricingPrice}>
              $250<span style={styles.pricingPeriod}>/month</span>
            </div>
            <div style={{backgroundColor: colors.secondary, color: colors.primary, padding: '5px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', display: 'inline-block', margin: '10px 0'}}>
              Monthly Plan
            </div>
            <ul style={styles.pricingFeatures}>
              <li>Single user</li>
              <li>All assessment types</li>
              <li>Detailed maturity reports</li>
              <li>Gap analysis</li>
              <li>Implementation Roadmaps</li>
              <li>Progress tracking</li>
              <li>Email support</li>
              <li>No long-term commitment</li>
            </ul>
            <Link to="/register" style={styles.pricingButton}>Get Started</Link>
          </div>
          
          <div style={styles.pricingCard}>
            <h3 style={styles.pricingTitle}>Annual Subscription</h3>
            <div style={styles.pricingPrice}>
              $2,500<span style={styles.pricingPeriod}>/year</span>
            </div>
            <div style={{backgroundColor: colors.secondary, color: colors.primary, padding: '5px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', display: 'inline-block', margin: '10px 0'}}>
              $500 OFF with code: NAV500OFF
            </div>
            <ul style={styles.pricingFeatures}>
              <li>Up to 5 users</li>
              <li>All assessment types</li>
              <li>Detailed maturity reports</li>
              <li>Gap analysis</li>
              <li>Implementation Roadmaps</li>
              <li>Progress tracking</li>
              <li>Email support</li>
              <li>Save $500 compared to monthly</li>
            </ul>
            <Link to="/register" style={styles.pricingButton}>Get Started</Link>
          </div>
          
          <div style={styles.pricingCard}>
            <h3 style={styles.pricingTitle}>Multi-Organization</h3>
            <div style={styles.pricingPrice}>
              $6,500<span style={styles.pricingPeriod}>/year</span>
            </div>
            <div style={{backgroundColor: colors.secondary, color: colors.primary, padding: '5px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', display: 'inline-block', margin: '10px 0'}}>
              $500 OFF with code: NAV500OFF
            </div>
            <ul style={styles.pricingFeatures}>
              <li>Up to 5 users</li>
              <li>Multiple organization management</li>
              <li>All assessment types</li>
              <li>Detailed maturity reports</li>
              <li>Gap analysis</li>
              <li>Implementation Roadmaps</li>
              <li>Progress tracking</li>
              <li>Priority support</li>
            </ul>
            <Link to="/register" style={styles.pricingButton}>Get Started</Link>
          </div>
        </div>
      </section>
      
      {/* Scenario TTX Section */}
      <section id="scenario-ttx" style={{...styles.section, backgroundColor: '#fff'}}>
        <h2 style={styles.sectionTitle}>Scenario TTX - Tabletop Exercise Simulator</h2>
        <div style={styles.sectionDivider}></div>
        <p style={styles.sectionDescription}>
          Our advanced tabletop exercise simulation platform helps organizations prepare for crisis events through realistic scenario-based training
        </p>
        
        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaUsers size={40} />
            </div>
            <h3 style={styles.featureTitle}>Team Training</h3>
            <p style={styles.featureDescription}>
              Train your crisis management team with realistic scenarios that test decision-making and communication under pressure.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaLaptop size={40} />
            </div>
            <h3 style={styles.featureTitle}>Virtual Exercises</h3>
            <p style={styles.featureDescription}>
              Conduct exercises remotely with team members in different locations, with real-time interaction and feedback.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaClipboardCheck size={40} />
            </div>
            <h3 style={styles.featureTitle}>Performance Tracking</h3>
            <p style={styles.featureDescription}>
              Measure and analyze team performance with detailed metrics and improvement recommendations after each exercise.
            </p>
          </div>
          
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>
              <FaExclamationTriangle size={40} />
            </div>
            <h3 style={styles.featureTitle}>Scenario Library</h3>
            <p style={styles.featureDescription}>
              Access a growing library of industry-specific crisis scenarios or create custom scenarios for your organization.
            </p>
          </div>
        </div>
        
        <div style={{maxWidth: '600px', margin: '40px auto 0', padding: '30px', backgroundColor: colors.secondary, borderRadius: '8px', textAlign: 'center'}}>
          <h3 style={{color: colors.primary, marginTop: 0, marginBottom: '20px'}}>Scenario TTX Subscription</h3>
          <p style={{margin: '10px 0', fontSize: '1.1rem', color: colors.primary}}>
            <strong>Annual Subscription:</strong> $7,500 (includes 1 Facilitator license)
          </p>
          <p style={{margin: '10px 0', fontSize: '1.1rem', color: colors.primary}}>
            <strong>Additional Facilitator licenses:</strong> $750 each
          </p>
          <p style={{margin: '15px 0', color: colors.primary}}>
            Run unlimited tabletop exercises, train team members, and conduct micro-simulations with exercises ranging from 1 to 20 injects.
          </p>
          
          <a href="https://outlook.office365.com/book/Pinnacle30Minute@pinnacleappinnovators.com/" target="_blank" rel="noopener noreferrer" style={{...styles.button, backgroundColor: colors.primary, color: colors.light, display: 'inline-block', marginTop: '10px', textDecoration: 'none'}}>
            Schedule a Demo
          </a>
        </div>
      </section>
      
      {/* Spacing div - Pricing section moved above */}
      <div style={{height: "40px"}}></div>
      
      {/* About Section */}
      <section id="about" style={styles.aboutSection}>
        <h2 style={styles.sectionTitle}>About PAI Consulting</h2>
        <div style={styles.sectionDivider}></div>
        
        <div style={styles.aboutContent}>
          <p style={styles.aboutText}>
            PAI Consulting specializes in resilience consulting services, helping organizations prepare for and respond to disruptions effectively. Our team of experts brings decades of experience in business continuity, disaster recovery, emergency management, and crisis management.
            <br /><br />
            <a 
              href="https://pinnacleappinnovators.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{...styles.externalLink, marginTop: '10px', display: 'inline-flex', justifyContent: 'center'}}
            >
              Visit our website <FaExternalLinkAlt size={14} />
            </a>
          </p>
          
          <h3 style={{...styles.featureTitle, marginTop: '40px'}}>Our Services Include:</h3>
          <ul style={styles.servicesList}>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Emergency Management
            </li>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Business Continuity Planning
            </li>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Disaster Recovery
            </li>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Crisis Management
            </li>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Third Party Risk Assessment
            </li>
            <li style={styles.serviceItem}>
              <FaCheckCircle style={styles.serviceIcon} /> Information Security Consulting
            </li>
          </ul>
        </div>
      </section>
      
      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <span style={styles.footerLogo}>Business Resilience Navigator</span>
            <p style={{color: '#aaa', lineHeight: '1.6', marginBottom: '20px'}}>
              A comprehensive assessment tool designed to help organizations enhance their resilience capabilities and prepare for disruptions.
            </p>
            <a 
              href="https://pinnacleappinnovators.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={styles.externalLink}
            >
              Visit PAI Consulting <FaExternalLinkAlt size={14} />
            </a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLink}>
                <a href="#features" style={styles.footerLinkAnchor}>Features</a>
              </li>
              <li style={styles.footerLink}>
                <a href="#pricing" style={styles.footerLinkAnchor}>Pricing</a>
              </li>
              <li style={styles.footerLink}>
                <a href="#scenario-ttx" style={styles.footerLinkAnchor}>Scenario TTX</a>
              </li>
              <li style={styles.footerLink}>
                <a href="#about" style={styles.footerLinkAnchor}>About Us</a>
              </li>
            </ul>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Account</h4>
            <ul style={styles.footerLinks}>
              <li style={styles.footerLink}>
                <Link to="/login" style={styles.footerLinkAnchor}>Login</Link>
              </li>
              <li style={styles.footerLink}>
                <Link to="/register" style={styles.footerLinkAnchor}>Register</Link>
              </li>
            </ul>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Business Resilience Navigator. All rights reserved.</p>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
